<template>
  <div ref="wrapper" class="embryo-sketch-wrapper">
    <div ref="sketchContainer" class="embryo-sketch-canvas"></div>

    <button
      v-for="hotspot in displayHotspots"
      :key="hotspot.id"
      :ref="`hotspot-${hotspot.id}`"
      class="embryo-hotspot"
      :class="{
        'embryo-hotspot--embryo': hotspot.type === 'embryo',
        'embryo-hotspot--guided':
          showGuidedOutline && hotspot.id === guidedHotspotId
      }"
      :style="hotspotStyle(hotspot)"
      :aria-label="hotspot.label"
      :aria-current="hotspot.id === guidedHotspotId ? 'true' : undefined"
      :tabindex="
        guidedHotspotId ? (hotspot.id === guidedHotspotId ? 0 : -1) : 0
      "
      :disabled="hotspot.fadeOut"
      @click="interactWithId(hotspot.id)"
      @keyup.enter="interactWithId(hotspot.id)"
      @keyup.space.prevent="interactWithId(hotspot.id)"
    ></button>
  </div>
</template>

<script>
import p5 from "p5";
import { randomRange, randomRangeInt, shuffleArray } from "@/helpers/random";

const CANVAS_SIZE = 560;
const SCOPE_RADIUS = 258;
const SCOPE_CENTER = CANVAS_SIZE / 2;
const NUM_EMBRYOS = 5;
const FRICTION = 0.88;
const EMBRYO_R = 8;
const OUTER_SHELL_R = 11;
const NUM_FRAGMENTS = 24;
const NUM_BUBBLES = 14;
const NUM_PARTICLES = 54;
const NUM_BG_TEXTURE_GRAINS = 1650;
const NUM_BG_TEXTURE_BLOBS = 140;
const NUM_BG_TOP_HAZE_BLOBS = 26;
const COLLISION_RESTITUTION = 0.35;
const MAX_SPIN = 0.24;

function randomPointInScope(margin = 0) {
  const a = randomRange(0, Math.PI * 2);
  const d = randomRange(0, SCOPE_RADIUS - margin);
  return {
    x: SCOPE_CENTER + Math.cos(a) * d,
    y: SCOPE_CENTER + Math.sin(a) * d
  };
}

function createDishTextureLayer(p) {
  const g = p.createGraphics(CANVAS_SIZE, CANVAS_SIZE);
  g.clear();
  g.noStroke();

  for (let i = 0; i < NUM_BG_TEXTURE_GRAINS; i++) {
    // Bias tiny grains toward lower half to mimic sediment distribution.
    let pt = randomPointInScope(3);
    let yNorm = (pt.y - (SCOPE_CENTER - SCOPE_RADIUS)) / (SCOPE_RADIUS * 2);
    while (randomRange(0, 1) > 0.28 + Math.pow(yNorm, 1.65)) {
      pt = randomPointInScope(3);
      yNorm = (pt.y - (SCOPE_CENTER - SCOPE_RADIUS)) / (SCOPE_RADIUS * 2);
    }

    const alpha = randomRange(7, 28) * (0.65 + yNorm * 0.7);
    const w = randomRange(0.8, 2.8);
    const h = randomRange(0.8, 2.2);
    g.fill(130, 104, 68, alpha);
    g.ellipse(pt.x, pt.y, w, h);
  }

  for (let i = 0; i < NUM_BG_TEXTURE_BLOBS; i++) {
    // Mid-scale blotches with slight bottom bias.
    let pt = randomPointInScope(12);
    const yNorm = (pt.y - (SCOPE_CENTER - SCOPE_RADIUS)) / (SCOPE_RADIUS * 2);
    if (randomRange(0, 1) < 0.52) {
      pt.y += randomRange(8, 38) * yNorm;
    }

    const alpha = randomRange(11, 32) * (0.8 + yNorm * 0.45);
    const w = randomRange(10, 32);
    const h = randomRange(6, 24);
    const rot = randomRange(0, Math.PI * 2);
    g.push();
    g.translate(pt.x, pt.y);
    g.rotate(rot);
    g.fill(126, 98, 62, alpha);
    g.ellipse(0, 0, w, h);
    g.pop();
  }

  for (let i = 0; i < NUM_BG_TOP_HAZE_BLOBS; i++) {
    const a = randomRange(Math.PI * 1.06, Math.PI * 1.94);
    const d = randomRange(SCOPE_RADIUS * 0.15, SCOPE_RADIUS * 0.95);
    const x = SCOPE_CENTER + Math.cos(a) * d;
    const y = SCOPE_CENTER + Math.sin(a) * d;
    const w = randomRange(SCOPE_RADIUS * 0.38, SCOPE_RADIUS * 0.86);
    const h = randomRange(SCOPE_RADIUS * 0.14, SCOPE_RADIUS * 0.42);
    const rot = randomRange(-0.45, 0.45);
    g.push();
    g.translate(x, y);
    g.rotate(rot);
    g.fill(98, 77, 48, randomRange(10, 24));
    g.ellipse(0, 0, w, h);
    g.pop();
  }

  return g;
}

function makeEmbryos() {
  const out = [];
  for (let i = 0; i < NUM_EMBRYOS; i++) {
    const angle = randomRange(0, Math.PI * 2);
    const dist = randomRange(20, SCOPE_RADIUS - OUTER_SHELL_R - 10);
    out.push({
      id: `embryo-${i}`,
      type: "embryo",
      x: SCOPE_CENTER + Math.cos(angle) * dist,
      y: SCOPE_CENTER + Math.sin(angle) * dist,
      r: OUTER_SHELL_R,
      vx: 0,
      vy: 0,
      collected: false,
      fadeOut: false,
      alpha: 255,
      variant: randomRangeInt(1, 4),
      rot: randomRange(0, Math.PI * 2),
      rotV: randomRange(-0.008, 0.008)
    });
  }
  return out;
}

function makeDebris() {
  const out = [];

  for (let i = 0; i < NUM_FRAGMENTS; i++) {
    const angle = randomRange(0, Math.PI * 2);
    const dist = randomRange(0, SCOPE_RADIUS - 30);
    out.push({
      id: `fragment-${i}`,
      type: "fragment",
      x: SCOPE_CENTER + Math.cos(angle) * dist,
      y: SCOPE_CENTER + Math.sin(angle) * dist,
      r: randomRange(8, 16),
      vx: 0,
      vy: 0,
      collected: false,
      fadeOut: false,
      alpha: 255,
      variant: randomRangeInt(0, 2),
      noiseOffsets: Array.from({ length: 8 }, () => randomRange(-6, 6)),
      rot: randomRange(0, Math.PI * 2),
      rotV: randomRange(-0.05, 0.05)
    });
  }

  for (let i = 0; i < NUM_BUBBLES; i++) {
    const angle = randomRange(0, Math.PI * 2);
    const dist = randomRange(0, SCOPE_RADIUS - 22);
    out.push({
      id: `bubble-${i}`,
      type: "bubble",
      x: SCOPE_CENTER + Math.cos(angle) * dist,
      y: SCOPE_CENTER + Math.sin(angle) * dist,
      r: randomRange(5, 12),
      vx: 0,
      vy: 0,
      collected: false,
      fadeOut: false,
      alpha: 255,
      variant: 0
    });
  }

  for (let i = 0; i < NUM_PARTICLES; i++) {
    const angle = randomRange(0, Math.PI * 2);
    const dist = randomRange(0, SCOPE_RADIUS - 8);
    const pointCount = randomRangeInt(5, 8);
    out.push({
      id: `particle-${i}`,
      type: "particle",
      x: SCOPE_CENTER + Math.cos(angle) * dist,
      y: SCOPE_CENTER + Math.sin(angle) * dist,
      r: randomRange(2, 5),
      vx: 0,
      vy: 0,
      collected: false,
      fadeOut: false,
      alpha: 255,
      variant: randomRangeInt(0, 1),
      shapeVariant: randomRangeInt(0, 3),
      aspect: randomRange(0.6, 1.9),
      jag: randomRange(0.08, 0.38),
      points: pointCount,
      noiseOffsets: Array.from({ length: pointCount }, () =>
        randomRange(-0.5, 0.5)
      ),
      rot: randomRange(0, Math.PI * 2),
      rotV: randomRange(-0.07, 0.07)
    });
  }

  return out;
}

export default {
  name: "SowOocyteSketch",

  props: {
    cpuTarget: {
      type: Number,
      default: 0
    },
    adaptiveQuality: {
      type: Boolean,
      default: true
    },
    isPrimed: {
      type: Boolean,
      default: false
    },
    guidedHotspotId: {
      type: String,
      default: ""
    },
    showGuidedOutline: {
      type: Boolean,
      default: true
    }
  },

  emits: [
    "specimen-collected",
    "all-collected",
    "interaction",
    "blocked",
    "needs-prime"
  ],

  data() {
    return {
      displayHotspots: []
    };
  },

  created() {
    // Keep p5 simulation entities non-reactive for frame performance.
    this._items = [...shuffleArray(makeDebris()), ...makeEmbryos()];
  },

  mounted() {
    const items = this._items;
    const self = this;

    const FRAGMENT_COLORS = [
      [159, 128, 81],
      [149, 119, 76],
      [168, 136, 88]
    ];
    const PARTICLE_COLORS = [
      [172, 136, 82],
      [150, 116, 68]
    ];

    const sketch = (p) => {
      let shellStroke;
      let hoveredItem = null;
      let warnItem = null;
      let warnTimer = 0;
      let pressedItem = null;
      let draggedItem = null;
      let didDrag = false;
      let lastDragX = 0;
      let lastDragY = 0;
      let dragOffsetX = 0;
      let dragOffsetY = 0;
      let burnSink = 0;
      let frameMsEma = 16.7;
      let qualityScale = 1;
      let dishTexture;

      function clamp(n, min, max) {
        return Math.max(min, Math.min(max, n));
      }

      function burnCpu(ms) {
        const end = performance.now() + ms;
        let local = burnSink;
        while (performance.now() < end) {
          // Intentional math work for stress testing render-thread load.
          local = Math.sin(local + 0.137) * 1000.0 + Math.cos(local * 0.001);
        }
        burnSink = local;
      }

      function tuneAdaptiveQuality(frameMs) {
        if (!self.adaptiveQuality) {
          qualityScale = 1;
          frameMsEma = frameMs;
          return;
        }

        frameMsEma = frameMsEma * 0.9 + frameMs * 0.1;

        if (frameMsEma > 24) {
          qualityScale = Math.max(0.35, qualityScale - 0.07);
        } else if (frameMsEma > 20) {
          qualityScale = Math.max(0.4, qualityScale - 0.04);
        } else if (frameMsEma > 17) {
          qualityScale = Math.max(0.45, qualityScale - 0.02);
        } else if (frameMsEma < 13) {
          qualityScale = Math.min(1, qualityScale + 0.025);
        } else if (frameMsEma < 15) {
          qualityScale = Math.min(1, qualityScale + 0.01);
        }
      }

      function backgroundStepFromQuality() {
        if (qualityScale > 0.86) return 1;
        if (qualityScale > 0.72) return 2;
        if (qualityScale > 0.58) return 3;
        if (qualityScale > 0.45) return 4;
        return 6;
      }

      function collisionStepFromQuality() {
        if (qualityScale > 0.84) return 1;
        if (qualityScale > 0.7) return 2;
        if (qualityScale > 0.56) return 3;
        return 4;
      }

      function ambientStrideFromQuality() {
        if (qualityScale > 0.84) return 1;
        if (qualityScale > 0.68) return 2;
        if (qualityScale > 0.52) return 3;
        return 4;
      }

      function hotspotIntervalFromQuality() {
        if (qualityScale > 0.82) return 3;
        if (qualityScale > 0.66) return 4;
        if (qualityScale > 0.52) return 5;
        return 7;
      }

      function distToCenter(x, y) {
        return Math.hypot(x - SCOPE_CENTER, y - SCOPE_CENTER);
      }

      function clampToScope(item) {
        const dx = item.x - SCOPE_CENTER;
        const dy = item.y - SCOPE_CENTER;
        const d = Math.hypot(dx, dy);
        const maxDist = SCOPE_RADIUS - item.r - 4;
        if (d > maxDist) {
          const angle = Math.atan2(dy, dx);
          item.x = SCOPE_CENTER + Math.cos(angle) * maxDist;
          item.y = SCOPE_CENTER + Math.sin(angle) * maxDist;
          item.vx *= -0.3;
          item.vy *= -0.3;
        }
      }

      function applyAmbientMotion(item) {
        if (item.type === "embryo") return;

        const dx = item.x - SCOPE_CENTER;
        const dy = item.y - SCOPE_CENTER;
        const d = Math.hypot(dx, dy) || 1;
        const tx = -dy / d;
        const ty = dx / d;
        const swirl = item.type === "particle" ? 0.012 : 0.007;
        const jitter = item.type === "particle" ? 0.02 : 0.012;

        item.vx += tx * swirl + randomRange(-jitter, jitter);
        item.vy += ty * swirl + randomRange(-jitter, jitter);
      }

      function resolveDebrisCollisions(collisionStep) {
        const active = items.filter(
          (item) => !item.collected && !item.fadeOut && item.r > 0
        );

        for (let i = 0; i < active.length; i += collisionStep) {
          for (let j = i + 1; j < active.length; j += collisionStep) {
            const a = active[i];
            const b = active[j];
            const aEmbryo = a.type === "embryo";
            const bEmbryo = b.type === "embryo";
            if (aEmbryo && bEmbryo) continue;

            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const d = Math.hypot(dx, dy) || 0.0001;
            const minDist = (a.r + b.r) * 0.9;

            if (d >= minDist) continue;

            const nx = dx / d;
            const ny = dy / d;
            const overlap = minDist - d;

            if (aEmbryo || bEmbryo) {
              const embryo = aEmbryo ? a : b;
              const debris = aEmbryo ? b : a;
              const dir = aEmbryo ? 1 : -1;
              debris.x += nx * overlap * 0.95 * dir;
              debris.y += ny * overlap * 0.95 * dir;
              embryo.x -= nx * overlap * 0.05 * dir;
              embryo.y -= ny * overlap * 0.05 * dir;
            } else {
              const sep = overlap * 0.5;
              a.x -= nx * sep;
              a.y -= ny * sep;
              b.x += nx * sep;
              b.y += ny * sep;
            }

            const rvx = b.vx - a.vx;
            const rvy = b.vy - a.vy;
            const velAlongNormal = rvx * nx + rvy * ny;

            if (velAlongNormal < 0) {
              const impulse =
                (-(1 + COLLISION_RESTITUTION) * velAlongNormal) / 2;
              const ix = impulse * nx;
              const iy = impulse * ny;
              a.vx -= ix;
              a.vy -= iy;
              b.vx += ix;
              b.vy += iy;
            }

            const tangentX = -ny;
            const tangentY = nx;
            const tangentVel = rvx * tangentX + rvy * tangentY;
            const spinKick = tangentVel * 0.018 + randomRange(-0.015, 0.015);

            if (a.rotV !== undefined) {
              a.rotV = Math.max(
                -MAX_SPIN,
                Math.min(MAX_SPIN, a.rotV - spinKick / Math.max(a.r * 0.45, 1))
              );
            }
            if (b.rotV !== undefined) {
              b.rotV = Math.max(
                -MAX_SPIN,
                Math.min(MAX_SPIN, b.rotV + spinKick / Math.max(b.r * 0.45, 1))
              );
            }

            clampToScope(a);
            clampToScope(b);
          }
        }
      }

      function hitTest(item, px, py) {
        return Math.hypot(px - item.x, py - item.y) < item.r + 6;
      }

      function topItemAt(px, py) {
        const hits = [];
        for (const item of items) {
          if (item.collected) continue;
          if (!hitTest(item, px, py)) continue;
          hits.push({
            item,
            d: Math.hypot(px - item.x, py - item.y)
          });
        }

        if (!hits.length) return null;

        const nonEmbryo = hits
          .filter((h) => h.item.type !== "embryo")
          .sort((a, b) => a.d - b.d);
        if (nonEmbryo.length) return nonEmbryo[0].item;

        hits.sort((a, b) => a.d - b.d);
        const top = hits[0].item;
        if (top.type === "embryo" && self.isObscured(top)) {
          const nearDebris = items
            .filter(
              (it) =>
                it.type !== "embryo" &&
                !it.collected &&
                !it.fadeOut &&
                Math.hypot(px - it.x, py - it.y) < it.r + 14
            )
            .sort(
              (a, b) =>
                Math.hypot(px - a.x, py - a.y) - Math.hypot(px - b.x, py - b.y)
            );
          if (nearDebris.length) return nearDebris[0];
        }

        return top;
      }

      function drawEmbryo(item) {
        p.push();
        p.translate(item.x, item.y);
        p.rotate(item.rot || 0);

        shellStroke.setAlpha(Math.min(item.alpha, 44));
        p.noFill();
        p.stroke(shellStroke);
        p.strokeWeight(0.54);
        p.ellipse(0, 0, OUTER_SHELL_R * 2, OUTER_SHELL_R * 2);

        p.noStroke();
        p.fill(184, 145, 93, Math.min(item.alpha, 232));
        p.ellipse(0, 0, EMBRYO_R * 2, EMBRYO_R * 2);

        p.fill(197, 157, 102, Math.min(item.alpha, 126));
        p.ellipse(0, -EMBRYO_R * 0.45, EMBRYO_R * 1.1, EMBRYO_R * 0.85);

        p.stroke(126, 99, 65, item.alpha * 0.22);
        p.strokeWeight(0.35);
        if (item.variant >= 1) {
          p.line(0, -EMBRYO_R * 0.7, 0, EMBRYO_R * 0.7);
        }
        if (item.variant >= 2) {
          p.line(-EMBRYO_R * 0.7, 0, EMBRYO_R * 0.7, 0);
        }
        if (item.variant >= 3) {
          const d = EMBRYO_R * 0.5;
          p.line(-d, -d, d, d);
          p.line(d, -d, -d, d);
        }

        p.pop();
      }

      function drawFragment(item) {
        const col = FRAGMENT_COLORS[item.variant] || FRAGMENT_COLORS[0];
        p.push();
        p.translate(item.x, item.y);
        p.rotate(item.rot || 0);
        p.fill(col[0], col[1], col[2], Math.min(item.alpha, 195));
        p.noStroke();
        p.beginShape();
        const pts = 8;
        for (let i = 0; i < pts; i++) {
          const angle = (i / pts) * Math.PI * 2;
          const offset = item.noiseOffsets[i];
          p.vertex(
            (item.r + offset) * Math.cos(angle),
            (item.r + offset * 0.7) * Math.sin(angle)
          );
        }
        p.endShape(p.CLOSE);
        p.pop();
      }

      function drawBubble(item) {
        p.push();
        p.noStroke();
        p.fill(176, 144, 96, Math.floor(item.alpha * 0.2));
        p.ellipse(item.x, item.y, item.r * 2, item.r * 2);
        p.stroke(206, 170, 112, Math.floor(item.alpha * 0.46));
        p.strokeWeight(0.8);
        p.noFill();
        p.ellipse(item.x, item.y, item.r * 2, item.r * 2);
        p.noStroke();
        p.fill(239, 210, 152, Math.floor(item.alpha * 0.55));
        p.ellipse(
          item.x - item.r * 0.3,
          item.y - item.r * 0.3,
          item.r * 0.4,
          item.r * 0.3
        );
        p.pop();
      }

      function drawParticle(item) {
        const col = PARTICLE_COLORS[item.variant] || PARTICLE_COLORS[0];
        p.push();
        p.translate(item.x, item.y);
        p.rotate(item.rot || 0);

        switch (item.shapeVariant) {
          case 0: {
            p.noStroke();
            p.fill(col[0], col[1], col[2], item.alpha);
            p.beginShape();
            for (let i = 0; i < item.points; i++) {
              const a = (i / item.points) * Math.PI * 2;
              const wobble = 1 + item.noiseOffsets[i] * item.jag;
              const rx = item.r * item.aspect;
              const ry = item.r / Math.max(item.aspect * 0.9, 0.5);
              p.vertex(rx * wobble * Math.cos(a), ry * wobble * Math.sin(a));
            }
            p.endShape(p.CLOSE);
            break;
          }
          case 1: {
            p.noStroke();
            p.fill(col[0], col[1], col[2], item.alpha);
            p.ellipse(0, 0, item.r * 2.3, item.r * (0.75 + item.aspect * 0.35));
            p.fill(col[0] - 6, col[1] - 6, col[2] - 6, item.alpha * 0.45);
            p.ellipse(item.r * 0.25, 0, item.r * 1.2, item.r * 0.55);
            break;
          }
          case 2: {
            p.stroke(col[0], col[1], col[2], item.alpha);
            p.strokeWeight(Math.max(1, item.r * 0.45));
            p.line(-item.r * 1.2, -item.r * 0.15, item.r * 1.2, item.r * 0.15);
            p.stroke(col[0] + 10, col[1] + 8, col[2] + 6, item.alpha * 0.35);
            p.strokeWeight(Math.max(0.8, item.r * 0.2));
            p.line(-item.r * 0.9, item.r * 0.35, item.r, item.r * 0.45);
            break;
          }
          default: {
            p.noStroke();
            p.fill(col[0], col[1], col[2], item.alpha * 0.9);
            p.ellipse(0, 0, item.r * 2.1, item.r * 1.05);
            p.fill(210, 195, 155, item.alpha * 0.25);
            p.ellipse(item.r * 0.35, 0, item.r * 1.1, item.r * 0.55);
            p.fill(226, 210, 168, item.alpha * 0.2);
            p.ellipse(item.r * 0.65, 0, item.r * 0.9, item.r * 0.4);
            break;
          }
        }

        p.pop();
      }

      function drawPipette(px, py) {
        p.push();
        p.translate(px, py);

        // Tip is anchored at cursor position and points toward top-left activity hotspot.
        p.rotate(Math.PI / 4);

        p.stroke(224, 236, 248, 230);
        p.strokeWeight(5);
        p.line(0, 0, 178, 0);

        p.stroke(174, 194, 212, 200);
        p.strokeWeight(2);
        p.line(0, -3, 178, -3);
        p.line(0, 3, 178, 3);

        p.noStroke();
        p.fill(242, 248, 255, 225);
        p.ellipse(0, 0, 8, 8);

        p.fill(220, 230, 240, 215);
        p.rect(178, -10, 74, 20, 5);

        p.stroke(145, 162, 178, 190);
        p.strokeWeight(1.2);
        for (let i = 0; i < 5; i++) {
          const x = 188 + i * 12;
          p.line(x, -8, x, -2);
          p.line(x, 2, x, 8);
        }

        p.stroke(214, 226, 238, 220);
        p.strokeWeight(3);
        p.line(252, 0, 296, 0);

        p.noStroke();
        p.fill(228, 236, 244, 230);
        p.rect(296, -12, 26, 24, 5);
        p.pop();
      }

      function drawScopeVignette() {
        const ctx = p.drawingContext;
        const grad = ctx.createRadialGradient(
          SCOPE_CENTER,
          SCOPE_CENTER,
          SCOPE_RADIUS * 0.5,
          SCOPE_CENTER,
          SCOPE_CENTER,
          SCOPE_RADIUS
        );
        grad.addColorStop(0, "rgba(0,0,0,0)");
        grad.addColorStop(0.72, "rgba(0,0,0,0.12)");
        grad.addColorStop(1, "rgba(0,0,0,0.62)");
        ctx.save();
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(SCOPE_CENTER, SCOPE_CENTER, SCOPE_RADIUS, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      function drawDishBase() {
        const ctx = p.drawingContext;
        ctx.save();
        ctx.beginPath();
        ctx.arc(SCOPE_CENTER, SCOPE_CENTER, SCOPE_RADIUS, 0, Math.PI * 2);
        ctx.clip();

        // Base vertical ramp: darker top to lighter lower field.
        const linear = ctx.createLinearGradient(
          SCOPE_CENTER,
          SCOPE_CENTER - SCOPE_RADIUS,
          SCOPE_CENTER,
          SCOPE_CENTER + SCOPE_RADIUS
        );
        linear.addColorStop(0, "#886C47");
        linear.addColorStop(0.54, "#B7925D");
        linear.addColorStop(1, "#E4BC78");
        ctx.fillStyle = linear;
        ctx.fillRect(
          SCOPE_CENTER - SCOPE_RADIUS,
          SCOPE_CENTER - SCOPE_RADIUS,
          SCOPE_RADIUS * 2,
          SCOPE_RADIUS * 2
        );

        // Warm center glow shifted slightly downward.
        const radial = ctx.createRadialGradient(
          SCOPE_CENTER,
          SCOPE_CENTER + SCOPE_RADIUS * 0.34,
          SCOPE_RADIUS * 0.08,
          SCOPE_CENTER,
          SCOPE_CENTER + SCOPE_RADIUS * 0.34,
          SCOPE_RADIUS * 0.96
        );
        radial.addColorStop(0, "rgba(236,194,122,0.95)");
        radial.addColorStop(0.42, "rgba(228,188,120,0.42)");
        radial.addColorStop(1, "rgba(136,108,71,0)");
        ctx.fillStyle = radial;
        ctx.fillRect(
          SCOPE_CENTER - SCOPE_RADIUS,
          SCOPE_CENTER - SCOPE_RADIUS,
          SCOPE_RADIUS * 2,
          SCOPE_RADIUS * 2
        );

        ctx.restore();
      }

      function drawDishTexture(t, qualityStep) {
        if (!dishTexture) return;

        const driftX = Math.sin(t * 0.16) * 0.9;
        const driftY = Math.cos(t * 0.13) * 0.7;
        let alpha = 92;
        if (qualityStep === 3) alpha = 74;
        if (qualityStep === 4) alpha = 62;
        if (qualityStep >= 6) alpha = 48;

        p.push();
        p.tint(255, alpha);
        p.image(dishTexture, driftX, driftY);
        p.tint(255, Math.max(20, alpha * 0.42));
        p.image(dishTexture, -driftX * 0.7, -driftY * 0.7);
        p.noTint();
        p.pop();
      }

      p.setup = function () {
        const cnv = p.createCanvas(CANVAS_SIZE, CANVAS_SIZE);
        cnv.style("cursor", "none");
        cnv.style("display", "block");
        shellStroke = p.color(156, 126, 84, 44);
        p.ellipseMode(p.CENTER);
        dishTexture = createDishTextureLayer(p);
      };

      p.draw = function () {
        const frameStart = performance.now();
        p.background(0, 0, 0);

        tuneAdaptiveQuality(p.deltaTime || 16.67);
        const backgroundStep = backgroundStepFromQuality();
        const collisionStep = collisionStepFromQuality();
        const ambientStride = ambientStrideFromQuality();
        const hotspotInterval = hotspotIntervalFromQuality();
        const t = p.frameCount * 0.02;

        drawDishBase();
        drawDishTexture(t, backgroundStep);

        p.noStroke();
        p.fill(238, 215, 165, 30);
        for (let i = 0; i < 9; i++) {
          p.ellipse(
            SCOPE_CENTER + Math.cos(i * 0.9) * 80,
            SCOPE_CENTER + Math.sin(i * 1.3) * 60,
            36 + i * 9,
            24 + i * 7
          );
        }

        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.collected) continue;
          if (item === draggedItem && !item.fadeOut) {
            const prevX = item.x;
            const prevY = item.y;
            item.x = p.mouseX + dragOffsetX;
            item.y = p.mouseY + dragOffsetY;
            clampToScope(item);
            // If the item actually moved, mark it as dragged to prevent
            // mouseReleased from treating this as a click
            if (Math.abs(item.x - prevX) + Math.abs(item.y - prevY) > 0.5) {
              didDrag = true;
            }
            item.vx = (item.x - prevX) * 0.8;
            item.vy = (item.y - prevY) * 0.8;
            if (item.rotV !== undefined) {
              item.rotV *= 0.94;
            }
            continue;
          }
          if (
            item.type === "embryo" ||
            (i + p.frameCount) % ambientStride === 0
          ) {
            applyAmbientMotion(item);
          }
          item.x += item.vx;
          item.y += item.vy;
          item.vx *= FRICTION;
          item.vy *= FRICTION;
          if (item.rot !== undefined) {
            item.rot += item.rotV || 0;
            if (item.type === "fragment" || item.type === "particle") {
              item.rotV += randomRange(-0.003, 0.003);
              item.rotV *= 0.985;
              if (Math.abs(item.rotV) < 0.0012) item.rotV = 0;
            } else {
              item.rotV *= 0.99;
            }
          }
          if (Math.abs(item.vx) < 0.05) item.vx = 0;
          if (Math.abs(item.vy) < 0.05) item.vy = 0;
          clampToScope(item);
          if (item.fadeOut) {
            item.alpha = Math.max(0, item.alpha - 14);
            if (item.alpha === 0) item.collected = true;
          }
        }

        resolveDebrisCollisions(collisionStep);

        hoveredItem = topItemAt(p.mouseX, p.mouseY);
        if (warnTimer > 0) warnTimer--;

        for (const item of items) {
          if (item.collected && item.alpha === 0) continue;

          if (item === hoveredItem && !item.fadeOut) {
            p.noStroke();
            p.fill(255, 240, 120, 90);
            p.ellipse(item.x, item.y, (item.r + 10) * 2, (item.r + 10) * 2);
          }
          if (item === warnItem && warnTimer > 0) {
            p.noStroke();
            p.fill(255, 80, 80, 120);
            p.ellipse(item.x, item.y, (item.r + 12) * 2, (item.r + 12) * 2);
          }

          if (item.type === "embryo") {
            drawEmbryo(item);
          } else if (item.type === "fragment") {
            drawFragment(item);
          } else if (item.type === "bubble") {
            drawBubble(item);
          } else if (item.type === "particle") {
            drawParticle(item);
          }
        }

        drawScopeVignette();

        if (
          p.mouseX > 0 &&
          p.mouseX < CANVAS_SIZE &&
          p.mouseY > 0 &&
          p.mouseY < CANVAS_SIZE
        ) {
          drawPipette(p.mouseX, p.mouseY);
        }

        if (p.frameCount % hotspotInterval === 0) {
          self.syncHotspots();
        }

        const cpuTargetFraction = clamp((self.cpuTarget || 0) / 100, 0, 0.95);
        if (cpuTargetFraction > 0) {
          const frameElapsed = performance.now() - frameStart;
          const rawFrameMs = p.deltaTime || 16.67;
          const isHidden = typeof document !== "undefined" && document.hidden;
          const maxStressFrameMs = 100;
          const shouldBurn =
            !isHidden && Number.isFinite(rawFrameMs) && rawFrameMs <= 250;

          if (!shouldBurn) return;

          const baselineFrameMs = clamp(
            rawFrameMs,
            1000 / 60,
            maxStressFrameMs
          );
          const targetBusyMs = baselineFrameMs * cpuTargetFraction;
          const extraWorkMs = clamp(
            targetBusyMs - frameElapsed,
            0,
            maxStressFrameMs
          );
          if (extraWorkMs > 0) {
            burnCpu(extraWorkMs);
          }
        }
      };

      p.mousePressed = function () {
        if (distToCenter(p.mouseX, p.mouseY) > SCOPE_RADIUS) return;
        const hit = topItemAt(p.mouseX, p.mouseY);
        if (!hit) return;
        pressedItem = hit;
        didDrag = false;
        lastDragX = p.mouseX;
        lastDragY = p.mouseY;
        if (hit.type !== "embryo") {
          draggedItem = hit;
          dragOffsetX = hit.x - p.mouseX;
          dragOffsetY = hit.y - p.mouseY;
        }
      };

      p.mouseDragged = function () {
        if (!draggedItem || draggedItem.collected || draggedItem.fadeOut)
          return;
        const dx = p.mouseX - lastDragX;
        const dy = p.mouseY - lastDragY;
        if (Math.abs(dx) + Math.abs(dy) > 0.5) {
          didDrag = true;
          if (draggedItem.rotV !== undefined) {
            draggedItem.rotV += randomRange(-0.08, 0.08);
          }
          self.$emit("interaction", draggedItem.type);
        }
        lastDragX = p.mouseX;
        lastDragY = p.mouseY;
      };

      p.mouseReleased = function () {
        if (pressedItem && !didDrag) {
          self.interactWith(pressedItem, p.mouseX, p.mouseY);
        }
        pressedItem = null;
        draggedItem = null;
        didDrag = false;
        dragOffsetX = 0;
        dragOffsetY = 0;
      };

      p.warnAt = function (item) {
        warnItem = item;
        warnTimer = 28;
      };
    };

    this.p5Instance = new p5(sketch, this.$refs.sketchContainer);
  },

  beforeUnmount() {
    if (this.p5Instance) {
      this.p5Instance.remove();
      this.p5Instance = null;
    }
  },

  methods: {
    shakeDish(intensity = 1) {
      const forceScale = Math.max(0.2, Math.min(2.5, intensity));
      for (const item of this._items) {
        if (item.collected || item.fadeOut) continue;
        const isEmbryo = item.type === "embryo";
        const localScale = isEmbryo ? forceScale * 0.5 : forceScale;
        const a = randomRange(0, Math.PI * 2);
        const f = randomRange(1.8, 4.2) * localScale;
        item.vx += Math.cos(a) * f;
        item.vy += Math.sin(a) * f;
        if (item.rotV !== undefined) {
          item.rotV += randomRange(-0.12, 0.12) * localScale;
        }
      }
      this.$emit("interaction", "shake");
    },

    interactWith(item, mx, my, forceMultiplier = 1) {
      if (item.collected || item.fadeOut) return;
      this.$emit("interaction", item.type);

      if (item.type === "embryo") {
        if (this.isObscured(item)) {
          if (this.p5Instance && this.p5Instance.warnAt) {
            this.p5Instance.warnAt(item);
          }
          this.$emit("blocked", item.id);
          return;
        }
        if (!this.isPrimed) {
          this.$emit("needs-prime", item.id);
          return;
        }
        item.fadeOut = true;
        const collected = this._items.filter(
          (it) => it.type === "embryo" && (it.collected || it.fadeOut)
        ).length;
        this.$emit("specimen-collected", collected);
        if (collected >= NUM_EMBRYOS) {
          setTimeout(() => this.$emit("all-collected"), 400);
        }
      } else {
        const clampedMultiplier = Math.max(
          0.15,
          Math.min(1.5, forceMultiplier)
        );
        const cx = mx !== undefined ? mx : item.x;
        const cy = my !== undefined ? my : item.y;
        const dx = item.x - cx;
        const dy = item.y - cy;
        const d = Math.hypot(dx, dy) || 1;
        const force = (7 + item.r * 0.25) * clampedMultiplier;
        item.vx = (dx / d) * force + randomRange(-1.5, 1.5) * clampedMultiplier;
        item.vy = (dy / d) * force + randomRange(-1.5, 1.5) * clampedMultiplier;
        if (item.rotV !== undefined) {
          item.rotV += randomRange(-0.18, 0.18) * clampedMultiplier;
        }
      }
    },

    interactWithId(id) {
      const item = this._items.find((it) => it.id === id);
      if (item) this.interactWith(item);
    },

    nudgeDebrisInArea(centerId, radius = 56) {
      const center = this._items.find(
        (it) => it.id === centerId && !it.collected && !it.fadeOut
      );
      if (!center) return 0;

      const movable = this._items.filter((it) => {
        if (it.type === "embryo") return false;
        if (it.collected || it.fadeOut) return false;
        return Math.hypot(it.x - center.x, it.y - center.y) <= radius;
      });

      if (!movable.length) return 0;

      for (const item of movable) {
        this.interactWith(item, center.x, center.y, 0.3);
      }

      return movable.length;
    },

    getGuidedTargets(pattern = "expanding-square") {
      const hotspots = this._items
        .filter((it) => !it.collected && !it.fadeOut)
        .map((it) => {
          const dx = it.x - SCOPE_CENTER;
          const dy = it.y - SCOPE_CENTER;
          const dist = Math.hypot(dx, dy);
          const ring = Math.min(5, Math.max(0, Math.floor(dist / 40)));
          const angle = (Math.atan2(dy, dx) + Math.PI * 2) % (Math.PI * 2);
          const sector = Math.floor(angle / (Math.PI / 4));
          return {
            id: it.id,
            type: it.type,
            ring,
            sector,
            distance: dist,
            obscured: it.type === "embryo" ? this.isObscured(it) : false,
            label: this.buildLabel(it)
          };
        });

      const sectorOrder = [0, 1, 2, 3, 4, 5, 6, 7];
      const sectorRank = (s) => {
        const idx = sectorOrder.indexOf(s);
        return idx === -1 ? 99 : idx;
      };

      if (pattern === "sector-scan") {
        return hotspots.sort((a, b) => {
          if (sectorRank(a.sector) !== sectorRank(b.sector)) {
            return sectorRank(a.sector) - sectorRank(b.sector);
          }
          if (a.ring !== b.ring) return a.ring - b.ring;
          if (a.distance !== b.distance) return a.distance - b.distance;
          return a.id.localeCompare(b.id);
        });
      }

      return hotspots.sort((a, b) => {
        if (a.ring !== b.ring) return a.ring - b.ring;
        if (sectorRank(a.sector) !== sectorRank(b.sector)) {
          return sectorRank(a.sector) - sectorRank(b.sector);
        }
        if (a.distance !== b.distance) return a.distance - b.distance;
        return a.id.localeCompare(b.id);
      });
    },

    evaluateCollectionFromTarget(targetId, maxSearchDistance = 64) {
      const target = this._items.find(
        (it) => it.id === targetId && !it.collected && !it.fadeOut
      );
      if (!target) {
        return { state: "none" };
      }

      const nearbyEmbryos = this._items
        .filter((it) => it.type === "embryo" && !it.collected && !it.fadeOut)
        .map((embryo) => ({
          embryo,
          distance: Math.hypot(embryo.x - target.x, embryo.y - target.y)
        }))
        .filter((entry) => entry.distance <= maxSearchDistance)
        .sort((a, b) => a.distance - b.distance);

      if (!nearbyEmbryos.length) {
        return { state: "none" };
      }

      const selected = nearbyEmbryos[0].embryo;
      if (this.isObscured(selected)) {
        const blocker = this.getUnblockingTarget(selected.id);
        return {
          state: "blocked",
          embryoId: selected.id,
          blockerId: blocker?.id || ""
        };
      }

      if (!this.isPrimed) {
        return {
          state: "unprimed",
          embryoId: selected.id
        };
      }

      return {
        state: "collectable",
        embryoId: selected.id
      };
    },

    getUnblockingTarget(embryoId) {
      const embryo = this._items.find((it) => it.id === embryoId);
      if (!embryo) return null;

      const candidates = this._items
        .filter((it) => {
          if (it.type === "embryo") return false;
          if (it.collected || it.fadeOut) return false;
          return (
            Math.hypot(it.x - embryo.x, it.y - embryo.y) < it.r + EMBRYO_R * 1.7
          );
        })
        .sort(
          (a, b) =>
            Math.hypot(a.x - embryo.x, a.y - embryo.y) -
            Math.hypot(b.x - embryo.x, b.y - embryo.y)
        );

      if (!candidates.length) return null;

      return {
        id: candidates[0].id,
        type: candidates[0].type,
        label: this.buildLabel(candidates[0])
      };
    },

    focusHotspot(id) {
      this.$nextTick(() => {
        const refEl = this.$refs[`hotspot-${id}`];
        const el = Array.isArray(refEl) ? refEl[0] : refEl;
        if (el && typeof el.focus === "function") {
          el.focus();
        }
      });
    },

    clearHotspotFocus() {
      this.$nextTick(() => {
        const active = document.activeElement;
        if (
          active &&
          active.classList &&
          active.classList.contains("embryo-hotspot") &&
          typeof active.blur === "function"
        ) {
          active.blur();
        }
      });
    },

    isObscured(embryo) {
      return this._items.some((other) => {
        if (other === embryo || other.collected || other.fadeOut) return false;
        if (other.type === "embryo") return false;
        return (
          Math.hypot(other.x - embryo.x, other.y - embryo.y) <
          other.r + EMBRYO_R * 0.85
        );
      });
    },

    syncHotspots() {
      this.displayHotspots = this._items
        .filter((it) => !it.collected)
        .map((it) => ({
          id: it.id,
          type: it.type,
          x: it.x,
          y: it.y,
          r: it.r,
          fadeOut: it.fadeOut,
          label: this.buildLabel(it)
        }));
    },

    buildLabel(item) {
      switch (item.type) {
        case "embryo": {
          const idx =
            this._items.filter((it) => it.type === "embryo").indexOf(item) + 1;
          const obscured = this.isObscured(item);
          return (
            `Sow oocyte ${idx} of ${NUM_EMBRYOS}` +
            (obscured
              ? ", partially hidden by debris"
              : ", visible - press to collect")
          );
        }
        case "fragment":
          return "Debris fragment - press to move";
        case "bubble":
          return "Air bubble - press to move";
        case "particle":
          return "Particulate matter - press to move";
        default:
          return "Item";
      }
    },

    hotspotStyle(hotspot) {
      const canvasEl =
        this.$refs.sketchContainer &&
        this.$refs.sketchContainer.querySelector("canvas");
      if (!canvasEl) return {};
      const canvasRect = canvasEl.getBoundingClientRect();
      const wrapRect = this.$refs.wrapper.getBoundingClientRect();
      const scale = canvasRect.width / CANVAS_SIZE;
      const left = canvasRect.left - wrapRect.left + hotspot.x * scale - 22;
      const top = canvasRect.top - wrapRect.top + hotspot.y * scale - 22;
      return { left: `${left}px`, top: `${top}px` };
    }
  }
};
</script>

<style scoped>
.embryo-sketch-wrapper {
  position: relative;
  display: inline-block;
}

.embryo-sketch-canvas canvas {
  display: block;
  border-radius: 50%;
  cursor: none;
  max-width: 100%;
  height: auto;
}

.embryo-hotspot {
  position: absolute;
  width: 44px;
  height: 44px;
  opacity: 0;
  border: none;
  background: transparent;
  padding: 0;
  cursor: none;
  z-index: 10;
  border-radius: 50%;
  pointer-events: none;
}

.embryo-hotspot:focus-visible {
  opacity: 1;
  outline: 3px solid #0d6efd;
  background: rgba(13, 110, 253, 0.15);
  cursor: default;
  pointer-events: auto;
}

.embryo-hotspot--embryo:focus-visible {
  outline-color: #e8c060;
  background: rgba(232, 192, 96, 0.2);
}

.embryo-hotspot--guided {
  opacity: 1;
  outline: 2px dashed rgba(13, 110, 253, 0.75);
  background: rgba(13, 110, 253, 0.1);
}
</style>
